import { Select, SelectProps } from 'antd'
import { useState } from 'react'

import { useClassifierServiceGetListInvestigationTestTypeEndpoint } from 'src/api/queries'
import { InvestigationTestTypeCategory } from 'src/api/requests'
import { DEFAULT_ONE_HUNDRED_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  /** A1 for 17.1, A2 for 18.1, A3 for 19.1. */
  category: InvestigationTestTypeCategory
} & SelectProps<string>

/** An investigation test type of the slot's category (17.1 / 18.1 / 19.1). */
export const HivTestTypeSelect = ({ category, ...props }: Props) => {
  const { t, currentLang } = useLocales()
  const [search, setSearch] = useState<string>()
  const debouncedSearch = useDebounce(search)

  const { data, isFetching } =
    useClassifierServiceGetListInvestigationTestTypeEndpoint(
      {
        pageNumber: 1,
        pageSize: DEFAULT_ONE_HUNDRED_PER_PAGE,
        searchTerm: debouncedSearch,
        types: [category]
      },
      [debouncedSearch, category],
      { staleTime: 1000 * 60 * 5 }
    )

  // Saved value first, so its label shows even when the search filters it out or its
  // category was changed after it was saved.
  const { data: selected } =
    useClassifierServiceGetListInvestigationTestTypeEndpoint(
      {
        pageNumber: 1,
        pageSize: 1,
        ids: props.value ? [props.value] : undefined
      },
      [props.value],
      { enabled: !!props.value, staleTime: 1000 * 60 * 5 }
    )

  const items = [...(selected?.data ?? []), ...(data?.data ?? [])].filter(
    (item, index, all) => all.findIndex((x) => x.id === item.id) === index
  )

  return (
    <Select
      allowClear
      showSearch
      filterOption={false}
      onSearch={setSearch}
      loading={isFetching}
      placeholder={t('common.select_from_list')}
      options={items.map((item) => ({
        value: item.id,
        label: item.title?.[currentLang.value]
      }))}
      {...props}
    />
  )
}
